import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { listMyEnrollments } from "../lib/db";
import { Calendar, Mail, User, Activity } from "lucide-react";

export default function Dashboard() {
  const { user, loading, logout } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate("/login");
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) {
      listMyEnrollments(user.user_id).then(setEnrollments).catch(() => {});
    }
  }, [user]);

  if (loading || !user) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-2 border-yellow-400 border-t-transparent animate-spin" />
      </div>
    );
  }

  const statusBadge = (s) => {
    const map = {
      pending: { c: "bg-yellow-400/15 text-yellow-300 border-yellow-400/30", t: "In attesa" },
      confirmed: { c: "bg-green-500/15 text-green-300 border-green-500/30", t: "Confermata" },
      rejected: { c: "bg-red-500/15 text-red-300 border-red-500/30", t: "Rifiutata" },
    };
    const m = map[s] || map.pending;
    return <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${m.c}`}>{m.t}</span>;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-12">
        <div>
          <div className="text-overline text-yellow-400 mb-2">// Area personale</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight">Ciao, {user.name?.split(" ")[0] || user.email}</h1>
          <p className="text-zinc-400 mt-2">Bentornato nel tuo spazio gialloblù.</p>
        </div>
        <button onClick={() => { logout(); navigate("/"); }} className="px-5 py-2.5 rounded-full border border-zinc-800 hover:border-zinc-600 text-sm text-zinc-300 hover:text-zinc-100 font-display font-semibold transition-colors w-fit" data-testid="dashboard-logout">
          Esci
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
        <div className="rounded-2xl bg-zinc-900 border border-zinc-800 p-6">
          <User className="h-6 w-6 text-yellow-400 mb-3" />
          <div className="text-overline text-zinc-500 mb-1">Profilo</div>
          <div className="font-display font-bold text-lg">{user.name || "—"}</div>
        </div>
        <div className="rounded-2xl bg-zinc-900 border border-zinc-800 p-6">
          <Mail className="h-6 w-6 text-yellow-400 mb-3" />
          <div className="text-overline text-zinc-500 mb-1">Email</div>
          <div className="font-display font-bold text-sm break-all">{user.email}</div>
        </div>
        <div className="rounded-2xl bg-zinc-900 border border-zinc-800 p-6">
          <Activity className="h-6 w-6 text-yellow-400 mb-3" />
          <div className="text-overline text-zinc-500 mb-1">Iscrizioni</div>
          <div className="font-display font-black text-3xl">{enrollments.length}</div>
        </div>
      </div>

      <div className="rounded-2xl bg-zinc-900 border border-zinc-800 overflow-hidden">
        <div className="px-6 py-5 border-b border-zinc-800">
          <h2 className="font-display font-bold text-xl uppercase tracking-tight">Le mie richieste di iscrizione</h2>
        </div>
        {enrollments.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <Calendar className="h-10 w-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-400 mb-1">Non hai ancora iscrizioni attive.</p>
            <button onClick={() => navigate("/palestra")} className="mt-3 inline-flex px-5 py-2.5 rounded-full bg-yellow-400 text-zinc-950 font-display font-bold text-sm hover:bg-yellow-300 transition-colors" data-testid="dashboard-browse-courses">
              Esplora i corsi
            </button>
          </div>
        ) : (
          <div className="divide-y divide-zinc-800">
            {enrollments.map((e) => (
              <div key={e.id} className="px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid={`enrollment-${e.id}`}>
                <div>
                  <div className="font-display font-bold">{e.course_name}</div>
                  <div className="text-xs text-zinc-500 mt-1">Inviata il {new Date(e.created_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}</div>
                </div>
                {statusBadge(e.status)}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
